import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useApp, t } from "@/lib/AppContext";
import { resolveImg } from "@/lib/api";
import FeedbackDialog from "./FeedbackDialog";

const NAV = [
  { to: "/", key: "home", label: "Главная" },
  { to: "/catalog", key: "catalog", label: "Каталог" },
  { to: "/configurator", key: "configurator", label: "Конфигуратор" },
  { to: "/services", key: "services", label: "Услуги" },
  { to: "/reviews", key: "reviews", label: "Отзывы" },
  { to: "/blog", key: "blog", label: "Блог" },
  { to: "/about", key: "about", label: "О компании" },
  { to: "/contacts", key: "contacts", label: "Контакты" },
];

export default function Header() {
  const { content, settings } = useApp();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const location = useLocation();

  const siteName = t(content, "header.site_name", "Везём авто из Европы");
  const ctaLabel = t(content, "header.cta_button", "Связаться");

  return (
    <>
      <header
        className="sticky top-0 z-40"
        style={{ background: "var(--color-secondary)", boxShadow: "var(--shadow-header)" }}
        data-testid="site-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 md:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group min-w-0" data-testid="header-logo">
            {settings?.logo_url ? (
              <img src={resolveImg(settings.logo_url)} alt={siteName} className="h-16 md:h-20 w-auto object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 md:w-10 md:h-10 rounded-md flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
                  <span className="font-headings font-black text-white text-lg leading-none">В</span>
                </div>
                <div className="leading-tight min-w-0">
                  <div className="font-headings font-extrabold text-white text-sm md:text-base tracking-tight truncate">{siteName}</div>
                  <div className="font-accent text-[10px] md:text-xs uppercase tracking-widest" style={{ color: "var(--color-gold)" }}>Европейские авто</div>
                </div>
              </div>
            )}
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                data-testid={`nav-${item.key}`}
              >
                {t(content, `header.nav.${item.key}`, item.label)}
              </NavLink>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="flex items-center gap-3">
            {settings?.phone && (
              <a
                href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`}
                className="hidden md:flex items-center gap-2 text-white/85 hover:text-[var(--color-gold)] transition-colors"
                data-testid="header-phone"
              >
                <Phone size={16} />
                <span className="font-accent text-sm font-semibold">{settings.phone}</span>
              </a>
            )}
            <button
              onClick={() => setFeedbackOpen(true)}
              className="hidden sm:inline-flex btn-primary-cta text-xs"
              style={{ padding: "10px 18px" }}
              data-testid="header-cta-button"
            >
              {ctaLabel}
            </button>

            {/* Mobile menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button className="lg:hidden text-white p-2" aria-label="Открыть меню" data-testid="mobile-menu-button">
                  <Menu size={24} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" hideClose className="border-0 p-0 w-[88vw] sm:w-[400px]" style={{ background: "var(--color-bg-dark)" }}>
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between p-5 border-b border-white/10">
                    <span className="font-headings font-extrabold text-white text-lg">Меню</span>
                    <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Закрыть">
                      <X size={22} />
                    </button>
                  </div>
                  <nav className="flex-1 overflow-y-auto px-5 py-2" data-testid="mobile-nav">
                    {NAV.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/"}
                        className={({ isActive }) => `mobile-nav-link ${isActive ? "text-[var(--color-gold)]" : ""}`}
                        onClick={() => setOpen(false)}
                        data-testid={`mobile-nav-${item.key}`}
                      >
                        {t(content, `header.nav.${item.key}`, item.label)}
                      </NavLink>
                    ))}
                  </nav>
                  <div className="p-5 border-t border-white/10 space-y-3">
                    {settings?.phone && (
                      <a href={`tel:${settings.phone.replace(/[^+\d]/g, "")}`} className="flex items-center gap-2 text-white">
                        <Phone size={16} />
                        <span className="font-accent font-semibold">{settings.phone}</span>
                      </a>
                    )}
                    <button
                      onClick={() => { setOpen(false); setFeedbackOpen(true); }}
                      className="btn-primary-cta w-full"
                      data-testid="mobile-cta-button"
                    >
                      {ctaLabel}
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
        {/* Tricolor strip — under header */}
        <div className="tricolor-strip" aria-hidden="true">
          <div /><div /><div />
        </div>
      </header>

      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
}
